/**
 * Kipify chat skills: type `/` in the input (Cursor-style) to open the picker.
 * Each skill maps to user intent the backend routes to the right tool.
 */
export type ChatSkill = {
  id: string;
  /** Slug after `/` (e.g. google-ads-search-term-analysis → `/google-ads-search-term-analysis`). */
  command: string;
  /** Title in the list. */
  label: string;
  /** Short description. */
  description: string;
  /** User message sent when the skill is chosen (LLM / tool routing). */
  submitText: string;
};

/** Typing or clicking this in chat opens the skills picker (`/skills` → all skills). */
export const CHAT_SKILLS_MENU_TRIGGER = '/skills';

export const CHAT_SKILLS: ChatSkill[] = [
  {
    id: 'google-ads-search-term-analysis',
    command: 'google-ads-search-term-analysis',
    label: 'Google Ads search term analysis',
    description:
      'Read-only search term audit: wasted spend first, negative exact/phrase suggestions, and Search keyword conflict checks (no automatic account changes).',
    submitText:
      'Run a read-only Google Ads search terms audit: surface queries wasting budget (e.g. spend with no conversions or CPA above threshold), suggest negative exact or phrase keywords where appropriate, and flag conflicts with active keywords. Do not change the account.',
  },
  {
    id: '404-link-checker',
    command: '404-link-checker',
    label: '404 Link Checker (Preview)',
    description:
      '[Preview] Read-only: scan destination URLs on Google Ads + Meta ads (default: active only). Ask for paused-only or all ads in chat if needed. Skips disconnected connectors.',
    submitText:
      'Run the 404 Link Checker (Preview, read-only): check destination URLs from my active Google Ads and Meta (Facebook) ads for 404s, broken links, and other HTTP errors. Skip any connector that is not connected; do not modify accounts.',
  },
  {
    id: 'facebook-creative-analysis',
    command: 'facebook-creative-analysis',
    label: 'Meta / Facebook creative analysis',
    description:
      'Read-only ad-level audit (metrics only, fast). Optional period in the same message; if omitted, backend default applies. Keep / monitor / cut; fatigue.',
    submitText:
      'Run a read-only Meta / Facebook creative analysis at ad level. Use the reporting period I state in this message when I include one; otherwise use the default period. Tell me which ads to keep, monitor, cut, or retest based on spend, ROAS, frequency, funnel actions (add to cart, checkout, purchases), and leads/CPL when present. After the table, give Meta-buyer-level next moves (testing design, creative hypotheses, delivery caveats) tied to specific Ad Ids from the data — not generic A/B or budget clichés. Flag creative fatigue only when supported by the data. Do not invent metrics or ad names; do not change the account.',
  },
  {
    id: 'kipify-advisor',
    command: 'kipify-advisor',
    label: 'Kipify growth pulse (by country)',
    description:
      'Spend, revenue, and efficiency by market — spot your winners, fix bleeders, and get a short action list. Add a date range in the same line if you want a custom window.',
    submitText:
      'Run the Kipify agent. Optional reporting period in this message; otherwise use the default window. Read-only.',
  },
  {
    id: 'meta-google-ad-copy-winners',
    command: 'meta-google-ad-copy-winners',
    label: 'Kipify skill — Meta + Google ad copy',
    description:
      'Kipify skill (read-only): Meta + Google Search RSA & Performance Max text vs the prior window; winners by ROAS/CTR/CAC when data allows; 3 funnel copy ideas per channel. Default period last 30 days — add From/To dates in the same message to override.',
    submitText:
      'Run the Kipify skill: Meta + Google ad copy. Read-only. Period: last 30 days.',
  },
  {
    id: 'product-margin-matrix',
    command: 'product-margin-matrix',
    label: 'Product Margin Matrix',
    description:
      'Merchandising matrix by Revenue x Margin (+ returns/stock risk). Start with 10 products per cluster and optional drill-down.',
    submitText:
      'Run the Product Margin Matrix: last 30 days.',
  },
  {
    id: 'discount-analysis',
    command: 'discount-analysis',
    label: 'Discount Analysis',
    description:
      'Read-only discount code profitability audit: trend, concentration by code, new vs repeat quality, geo sensitivity, and final over-discounting summary.',
    submitText:
      'Run the Discount Analysis: last 120 days. Read-only.',
  },
];

/**
 * Filter skills by what the user typed after `/` (case insensitive).
 */
export function filterChatSkills(queryAfterSlash: string): ChatSkill[] {
  const q = queryAfterSlash.trim().toLowerCase();
  if (!q || q === 'skills') return CHAT_SKILLS;
  return CHAT_SKILLS.filter((s) => {
    const hay = `${s.command} ${s.label} ${s.description}`.toLowerCase();
    return (
      s.command.toLowerCase().includes(q) ||
      s.label.toLowerCase().includes(q) ||
      hay.includes(q)
    );
  });
}

/** True when input is single-line slash-command mode (starts with `/`). */
export function isSlashCommandInput(value: string): boolean {
  return !value.includes('\n') && value.startsWith('/');
}
